import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, SlidersHorizontal, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import BusinessCard from "../components/BusinessCard";
import BusinessCardSkeleton from "../components/BusinessCardSkeleton";
import SearchAutocomplete from "../components/SearchAutocomplete";
import GeoLocateButton from "../components/GeoLocateButton";
import EmptySearchState from "../components/EmptySearchState";
import Pagination from "../components/Pagination";
import { CATEGORY_META } from "../lib/constants";
import { trackEvent } from "../lib/analytics";

const PRICES = ["$", "$$", "$$$", "$$$$"];
const PAGE_SIZE = 20;

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const searchInputRef = useRef(null);

  const q = params.get("q") || "";
  const category = params.get("category") || "";
  const city = params.get("city") || "";
  const neighborhood = params.get("neighborhood") || "";
  const price = params.get("price") || "";
  const minRating = params.get("min_rating") || "";
  const sort = params.get("sort") || "rating";
  const page = parseInt(params.get("page") || "1", 10);

  // Defensive: normalize malformed URLs like /search?category=banques?category=banques.
  // Browsers parse the *second* `?` as part of the previous value, leaving "banques?category=banques"
  // in the param. Strip the "?..." tail and rewrite history once.
  useEffect(() => {
    const dirty = ["q", "category", "city", "neighborhood", "price", "min_rating"]
      .some((k) => (params.get(k) || "").includes("?"));
    if (!dirty) return;
    const clean = new URLSearchParams();
    params.forEach((v, k) => {
      const fixed = v.includes("?") ? v.split("?")[0] : v;
      if (fixed) clean.set(k, fixed);
    });
    setParams(clean, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    if (key !== "page") next.delete("page");
    setParams(next, { replace: true });
  };

  const setPage = (p) => {
    const next = new URLSearchParams(params);
    next.set("page", String(p));
    setParams(next, { replace: true });
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/cities");
      setCities(data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!city) { setNeighborhoods([]); return; }
      const { data } = await api.get("/neighborhoods", { params: { city_slug: city } });
      setNeighborhoods(data);
    })();
  }, [city]);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      const p = { sort, page, limit: PAGE_SIZE };
      if (q) p.q = q;
      if (category) p.category = category;
      if (city) p.city = city;
      if (neighborhood) p.neighborhood = neighborhood;
      if (price) p.price = price;
      if (minRating) p.min_rating = minRating;
      const { data } = await api.get("/businesses", { params: p });
      setData(data);
      setLoading(false);
    };
    fetchData();
  }, [q, category, city, neighborhood, price, minRating, sort, page]);

  // Fire a single `search` analytics event when the user pauses typing (≥800ms).
  useEffect(() => {
    if (!q || q.length < 2) return;
    const t = setTimeout(() => {
      trackEvent("search", {
        search_term: q,
        city,
        category,
        source: "search_page",
        results_total: data.total,
      });
    }, 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const activeCat = useMemo(() => category ? CATEGORY_META[category] : null, [category]);

  return (
    <div className="bg-lokaso-sand min-h-screen" data-testid="search-page">
      {/* Sticky search header */}
      <div className="sticky top-16 z-30 bg-lokaso-green text-lokaso-sand shadow-md" data-testid="search-header">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-2">
          <Link to="/" className="p-2 rounded-full hover:bg-white/10" data-testid="search-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1 flex items-center gap-2 px-3 bg-white text-lokaso-dark rounded-xl h-11 relative" ref={searchInputRef}>
            <Search className="w-4 h-4 text-stone-500" />
            <input
              value={q}
              onChange={(e) => { setParam("q", e.target.value); setShowAutocomplete(true); }}
              onFocus={() => setShowAutocomplete(true)}
              placeholder="Rechercher une entreprise..."
              className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-stone-400"
              data-testid="search-input"
              autoComplete="off"
            />
            {showAutocomplete && (
              <SearchAutocomplete
                query={q}
                citySlug={city}
                onClose={() => setShowAutocomplete(false)}
                onSelect={() => setShowAutocomplete(false)}
              />
            )}
          </div>
          <GeoLocateButton
            onLocated={({ city: c }) => {
              setParam("city", c || "");
              setParam("neighborhood", "");
            }}
          />
          <select
            value={sort}
            onChange={(e) => setParam("sort", e.target.value)}
            className="h-11 px-3 rounded-xl bg-white text-lokaso-dark text-sm focus:outline-none"
            data-testid="search-sort-select"
          >
            <option value="rating">Mieux notés</option>
            <option value="reviews">Plus d'avis</option>
            <option value="recent">Plus récents</option>
          </select>
          <button
            onClick={() => setShowFilters((s) => !s)}
            className="md:hidden p-2 rounded-xl bg-white/10 hover:bg-white/20"
            data-testid="search-filters-toggle"
          >
            <SlidersHorizontal className="w-5 h-5" />
          </button>
        </div>

        {/* Category pills */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 pb-3 flex gap-2 overflow-x-auto no-scrollbar" data-testid="category-pills-row">
          <button
            onClick={() => setParam("category", "")}
            className={`pill ${!category ? "pill-active" : ""}`}
            data-testid="category-pill-all"
          >
            Tout
          </button>
          {Object.entries(CATEGORY_META).map(([slug, meta]) => (
            <button
              key={slug}
              onClick={() => setParam("category", slug)}
              className={`pill ${category === slug ? "pill-active" : ""}`}
              style={category === slug ? { background: meta.color, borderColor: meta.color, color: "white" } : {}}
              data-testid={`category-pill-${slug}`}
            >
              <span className="text-base">{meta.emoji}</span>
              <span>{meta.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 grid md:grid-cols-[260px_1fr] gap-8">
        {/* Filters */}
        <aside className={`${showFilters ? "block" : "hidden"} md:block`} data-testid="filters-sidebar">
          <div className="bg-white rounded-2xl border border-stone-200 p-5 sticky top-44">
            <h3 className="font-display text-lg font-bold mb-4">Filtres</h3>

            <div className="mb-5">
              <label className="text-xs uppercase tracking-wider font-semibold text-stone-500">Ville</label>
              <select
                value={city}
                onChange={(e) => { setParam("city", e.target.value); setParam("neighborhood", ""); }}
                className="mt-1 w-full h-11 px-3 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-lokaso-green"
                data-testid="filter-city"
              >
                <option value="">Toutes</option>
                {cities.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>

            <div className="mb-5">
              <label className="text-xs uppercase tracking-wider font-semibold text-stone-500">Quartier</label>
              <select
                value={neighborhood}
                onChange={(e) => setParam("neighborhood", e.target.value)}
                disabled={!city}
                className="mt-1 w-full h-11 px-3 rounded-xl border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-lokaso-green disabled:bg-stone-50"
                data-testid="filter-neighborhood"
              >
                <option value="">Tous</option>
                {neighborhoods.map((n) => <option key={n.slug} value={n.slug}>{n.name}</option>)}
              </select>
            </div>

            <div className="mb-5">
              <label className="text-xs uppercase tracking-wider font-semibold text-stone-500">Gamme de prix</label>
              <div className="mt-2 flex gap-2">
                {PRICES.map((p) => (
                  <button
                    key={p}
                    onClick={() => setParam("price", price === p ? "" : p)}
                    className={`flex-1 h-10 rounded-xl border text-sm font-semibold ${price === p ? "bg-lokaso-green text-white border-lokaso-green" : "border-stone-200 hover:bg-stone-50"}`}
                    data-testid={`filter-price-${p.length}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-5">
              <label className="text-xs uppercase tracking-wider font-semibold text-stone-500">Note minimum</label>
              <div className="mt-2 flex flex-col gap-2">
                {[
                  { v: "4", l: "★★★★+ et plus" },
                  { v: "3", l: "★★★+ et plus" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    onClick={() => setParam("min_rating", minRating === opt.v ? "" : opt.v)}
                    className={`h-10 rounded-xl border text-sm text-left px-3 ${minRating === opt.v ? "bg-lokaso-green text-white border-lokaso-green" : "border-stone-200 hover:bg-stone-50"}`}
                    data-testid={`filter-rating-${opt.v}`}
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => setParams(new URLSearchParams())}
              className="text-sm text-stone-600 underline hover:text-lokaso-red"
              data-testid="filter-clear"
            >
              Réinitialiser
            </button>
          </div>
        </aside>

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-stone-600" data-testid="results-count">
              <span className="font-bold text-lokaso-dark">{data.total}</span> résultat{data.total > 1 ? "s" : ""} {activeCat ? <>dans <span className="font-semibold" style={{ color: activeCat.color }}>{activeCat.name}</span></> : ""}
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="search-skeletons">
              {Array.from({ length: 6 }).map((_, i) => (
                <BusinessCardSkeleton key={`skeleton-${i}`} />
              ))}
            </div>
          ) : data.items.length === 0 ? (
            <EmptySearchState
              query={q}
              category={category}
              city={city}
              onClearCategory={() => setParam("category", "")}
              onClearCity={() => { setParam("city", ""); setParam("neighborhood", ""); }}
              onResetAll={() => setParams(new URLSearchParams())}
            />
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="search-results">
                {data.items.map((b) => <BusinessCard key={b.id} business={b} />)}
              </div>
              <Pagination page={data.page || page} totalPages={data.total_pages || 1} onPageChange={setPage} />
              <p className="mt-4 text-center text-xs text-stone-500" data-testid="pagination-summary">
                Page {data.page || page} sur {data.total_pages || 1} · {PAGE_SIZE} résultats par page
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
